import { Helmet } from 'react-helmet-async';

/**
 * Structured data block for any Bibliothèque HÉRITA page.
 *
 * Emits up to three JSON-LD blocks in a single Helmet pass:
 *   - Article (the educational page itself)
 *   - BreadcrumbList (helps Google nest the page under /)
 *   - FAQPage (only if `faq` is provided)
 *
 * Reusable: pass `path`, `title`, `description`, `breadcrumb`, `faq`,
 * and optionally `datePublished`/`dateModified` (defaults today).
 */
const SITE_URL = 'https://herita.me';

const LibraryStructuredData = ({
  path,
  title,
  description,
  breadcrumb = [],
  faq = [],
  howTo = null,
  datePublished = '2026-02-18',
  dateModified = '2026-02-18',
}) => {
  const pageUrl = `${SITE_URL}${path}`;

  const article = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    inLanguage: 'fr-FR',
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    datePublished,
    dateModified,
    author: { '@type': 'Organization', name: 'HÉRITA', url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'HÉRITA',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/icon-512.png`,
      },
    },
    image: `${SITE_URL}/og-image.jpg?v=2`,
  };

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.label,
      item: item.to ? `${SITE_URL}${item.to}` : pageUrl,
    })),
  };

  const faqPage =
    faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((q) => ({
            '@type': 'Question',
            name: q.q,
            acceptedAnswer: { '@type': 'Answer', text: q.a },
          })),
        }
      : null;

  // HowTo — emitted only when the guide describes an actual sequence of
  // user-facing steps. `howTo.steps` is an array of { name, text }.
  const howToJsonLd =
    howTo && Array.isArray(howTo.steps) && howTo.steps.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: howTo.name,
          description: howTo.description || description,
          inLanguage: 'fr-FR',
          totalTime: howTo.totalTime || 'PT5M',
          step: howTo.steps.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.name,
            text: s.text,
            url: s.url || `${pageUrl}#etape-${i + 1}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(article)}
      </script>
      {breadcrumb.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbList)}
        </script>
      )}
      {faqPage && (
        <script type="application/ld+json">
          {JSON.stringify(faqPage)}
        </script>
      )}
      {howToJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(howToJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default LibraryStructuredData;
